import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// コースの流れを図示するセクション。
// 言語交換 → 家庭教師補助 → レベル別（A / B1 / B2 / C）の段階を示す。
// 文言は i18n（home 名前空間 courseFlow.*）で外部化。
// 既存セクション（PhilosophySection / CtaBanner）と同じ
// IntersectionObserver によるフェードイン・配色・フォント流儀を踏襲。

interface Step {
  badge: string;
  title: string;
  desc: string;
  icon: string;
}

export default function CourseFlowSection() {
  const { t } = useTranslation("home");
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const STEPS: Step[] = [
    {
      badge: "STEP 1",
      title: t("courseFlow.step1Title"),
      desc: t("courseFlow.step1Desc"),
      icon: "ri-chat-3-line",
    },
    {
      badge: "STEP 2",
      title: t("courseFlow.step2Title"),
      desc: t("courseFlow.step2Desc"),
      icon: "ri-user-star-line",
    },
  ];

  // レベル別コースの段階。
  const LEVELS: { code: string; label: string }[] = [
    { code: "A", label: t("courseFlow.levelA") },
    { code: "B1", label: t("courseFlow.levelB1") },
    { code: "B2", label: t("courseFlow.levelB2") },
    { code: "C", label: t("courseFlow.levelC") },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="course-flow"
      ref={ref}
      style={{ background: "#FAF5E8", padding: "100px 0" }}
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Section label */}
        <div
          className="flex items-center gap-3 mb-6"
          style={{ opacity: visible ? 1 : 0, transition: "opacity 0.8s ease" }}
        >
          <div style={{ width: 3, height: 24, background: "#2C5F4F", borderRadius: 2 }} />
          <span
            style={{
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: "0.14em",
              color: "#2C5F4F",
              textTransform: "uppercase",
            }}
          >
            {t("courseFlow.label")}
          </span>
        </div>

        <h2
          style={{
            fontFamily: "'Noto Sans JP', sans-serif",
            fontWeight: 800,
            fontSize: "clamp(22px, 2.8vw, 32px)",
            color: "#1A1A1A",
            lineHeight: 1.5,
            marginBottom: 16,
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(16px)",
            transition: "opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s",
          }}
        >
          {t("courseFlow.heading")}
        </h2>
        <p
          style={{
            fontFamily: "'Noto Serif JP', serif",
            fontSize: 15,
            color: "#666666",
            lineHeight: 1.9,
            maxWidth: 620,
            marginBottom: 56,
            opacity: visible ? 1 : 0,
            transition: "opacity 1s ease 0.2s",
          }}
        >
          {t("courseFlow.intro")}
        </p>

        {/* Flow: STEP1 → STEP2 → レベル別 */}
        <div
          className="flex flex-col lg:flex-row items-stretch gap-6"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(24px)",
            transition: "opacity 0.9s ease 0.3s, transform 0.9s ease 0.3s",
          }}
        >
          {STEPS.map((step) => (
            <div key={step.badge} className="flex flex-col lg:flex-row items-stretch gap-6 lg:contents">
              {/* Step card */}
              <div
                style={{
                  flex: 1,
                  background: "#ffffff",
                  borderRadius: 20,
                  padding: "32px 28px",
                  border: "1px solid rgba(44,95,79,0.1)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className="flex items-center justify-center"
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: "rgba(44,95,79,0.08)",
                      color: "#2C5F4F",
                    }}
                  >
                    <i className={step.icon} style={{ fontSize: 20 }} />
                  </div>
                  <span
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.12em",
                      color: "#2C5F4F",
                      textTransform: "uppercase",
                    }}
                  >
                    {step.badge}
                  </span>
                </div>
                <h3
                  style={{
                    fontFamily: "'Noto Sans JP', sans-serif",
                    fontWeight: 700,
                    fontSize: 18,
                    color: "#1A1A1A",
                    marginBottom: 10,
                  }}
                >
                  {step.title}
                </h3>
                <p
                  style={{
                    fontFamily: "'Noto Serif JP', serif",
                    fontSize: 14,
                    color: "#666666",
                    lineHeight: 1.9,
                  }}
                >
                  {step.desc}
                </p>
              </div>

              {/* Arrow between steps (desktop) */}
              <div
                className="hidden lg:flex items-center justify-center"
                style={{ flex: "0 0 auto", color: "#2C5F4F" }}
                aria-hidden="true"
              >
                <i
                  className="ri-arrow-right-line"
                  style={{ fontSize: 22, opacity: 0.5 }}
                />
              </div>
              {/* Arrow between steps (mobile) */}
              <div
                className="flex lg:hidden items-center justify-center"
                style={{ color: "#2C5F4F" }}
                aria-hidden="true"
              >
                <i
                  className="ri-arrow-down-line"
                  style={{ fontSize: 20, opacity: 0.5 }}
                />
              </div>
            </div>
          ))}

          {/* レベル別コース（STEP 3） */}
          <div
            style={{
              flex: 1.4,
              borderRadius: 20,
              background: "linear-gradient(135deg, #2C5F4F 0%, #3A7A63 100%)",
              padding: "32px 28px",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 180,
                height: 180,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.06)",
              }}
            />
            <div className="flex items-center gap-3 mb-5" style={{ position: "relative" }}>
              <div
                className="flex items-center justify-center"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.15)",
                  color: "#ffffff",
                }}
              >
                <i className="ri-bar-chart-grouped-line" style={{ fontSize: 20 }} />
              </div>
              <span
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.12em",
                  color: "rgba(255,255,255,0.7)",
                  textTransform: "uppercase",
                }}
              >
                STEP 3
              </span>
            </div>
            <h3
              style={{
                fontFamily: "'Noto Sans JP', sans-serif",
                fontWeight: 700,
                fontSize: 18,
                color: "#ffffff",
                marginBottom: 16,
                position: "relative",
              }}
            >
              {t("courseFlow.levelCourseTitle")}
            </h3>
            <div className="flex flex-wrap gap-2" style={{ position: "relative" }}>
              {LEVELS.map((lv) => (
                <div
                  key={lv.code}
                  className="flex items-center gap-2"
                  style={{
                    padding: "8px 14px",
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.12)",
                    border: "1px solid rgba(255,255,255,0.18)",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontWeight: 700,
                      fontSize: 13,
                      color: "#ffffff",
                    }}
                  >
                    {lv.code}
                  </span>
                  <span
                    style={{
                      fontFamily: "'Noto Sans JP', sans-serif",
                      fontSize: 12,
                      color: "rgba(255,255,255,0.8)",
                    }}
                  >
                    {lv.label}
                  </span>
                </div>
              ))}
            </div>
            <p
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontSize: 13,
                color: "rgba(255,255,255,0.8)",
                lineHeight: 1.9,
                marginTop: 18,
                position: "relative",
              }}
            >
              {t("courseFlow.levelNote")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
